import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2, RefreshCcw, Linkedin, AlertCircle, DollarSign, Users as UsersIcon, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { AppShell } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';

import { api } from '../lib/api';
import { getErrorMessage } from '../lib/errors';
import { useAuth } from '../lib/auth-context';

// Format an ISO timestamp into a compact relative + absolute string.
function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const sec = Math.round(diff / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);
  let rel;
  if (sec < 60) rel = `${sec}s ago`;
  else if (min < 60) rel = `${min}m ago`;
  else if (hr < 48) rel = `${hr}h ago`;
  else rel = `${day}d ago`;
  return rel;
}

function fmtAbs(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function roleBadgeClass(role) {
  if (role === 'admin') return 'bg-primary text-primary-foreground';
  if (role === 'editor') return 'bg-[hsl(var(--sky))] text-foreground border border-border';
  return 'bg-secondary text-foreground border border-border';
}

function SummaryCard({ icon: Icon, label, value, sub, accent = 'foreground' }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center bg-${accent}/10 text-${accent}`}>
            <Icon size={20} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
            <div className="text-2xl font-bold tabular-nums">{value}</div>
            {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminApifyUsagePage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const { data } = await api.get('/admin/apify-usage');
      setItems(data.items || []);
      setSummary(data.summary || null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load Apify usage'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh every 30s while page is open
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, []);

  const sortedItems = useMemo(() => {
    return [...(items || [])].sort((a, b) => (b.total_calls || 0) - (a.total_calls || 0));
  }, [items]);

  const successRate = useMemo(() => {
    if (!summary || !summary.total_calls) return null;
    return ((summary.total_success / summary.total_calls) * 100).toFixed(1);
  }, [summary]);

  // Auth guard — admin/editor only (AFTER all hooks per Rules of Hooks)
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin' && user.role !== 'editor') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8" data-testid="admin-apify-usage-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Badge className="bg-card border border-border text-foreground">
                <Linkedin size={11} className="mr-1" /> Admin · Apify usage
              </Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3">
              LinkedIn scraper usage
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Per-user breakdown of LinkedIn URL scrapes. Each successful scrape
              consumes ~${summary?.cost_per_scrape_usd ?? 0.01} on your Apify
              account ({summary?.daily_cap ?? 2}/user/day rate-limited).
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load()}
            disabled={refreshing}
            className="gap-1.5"
            data-testid="admin-apify-refresh-btn"
          >
            {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
            Refresh
          </Button>
        </div>

        {/* Summary tiles */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-apify-summary">
            <SummaryCard
              icon={UsersIcon}
              label="Users tracked"
              value={summary?.users_tracked ?? 0}
              sub={`${(items || []).filter((i) => (i.daily_count || 0) > 0).length} active today`}
            />
            <SummaryCard
              icon={CheckCircle2}
              label="Total successes"
              value={summary?.total_success ?? 0}
              sub={successRate !== null ? `${successRate}% success rate` : 'No calls yet'}
            />
            <SummaryCard
              icon={XCircle}
              label="Total failures"
              value={summary?.total_failure ?? 0}
              sub={summary?.total_calls ? `of ${summary.total_calls} calls` : ''}
            />
            <SummaryCard
              icon={DollarSign}
              label="Estimated cost"
              value={`$${(summary?.estimated_cost_usd ?? 0).toFixed(2)}`}
              sub={`@ $${summary?.cost_per_scrape_usd ?? 0.01}/scrape`}
            />
          </div>
        )}

        {/* Empty state */}
        {!loading && (items || []).length === 0 && (
          <Card data-testid="admin-apify-empty">
            <CardContent className="py-16 text-center">
              <Linkedin size={48} className="mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-base font-semibold">No Apify scrapes yet</p>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
                Usage will appear here as soon as a paid user (or admin) calls{' '}
                <code className="text-[11px] bg-muted px-1.5 py-0.5 rounded">POST /api/linkedin/scrape</code>.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Usage table */}
        {!loading && (items || []).length > 0 && (
          <Card data-testid="admin-apify-table-card">
            <CardContent className="p-0">
              <ScrollArea className="max-h-[60vh]">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead className="min-w-[200px]">User</TableHead>
                      <TableHead className="text-right">Today</TableHead>
                      <TableHead className="text-right">Total calls</TableHead>
                      <TableHead className="text-right">Success</TableHead>
                      <TableHead className="text-right">Failures</TableHead>
                      <TableHead className="text-right">Est. cost</TableHead>
                      <TableHead className="min-w-[140px]">Last call</TableHead>
                      <TableHead className="min-w-[120px]">Last failure</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedItems.map((row) => {
                      const successRatePct = row.total_calls
                        ? Math.round((row.total_success / row.total_calls) * 100)
                        : null;
                      const atCap = (row.daily_count || 0) >= (summary?.daily_cap || 2);
                      return (
                        <TableRow key={row.user_id} data-testid={`admin-apify-row-${row.user_id}`}>
                          <TableCell className="font-medium">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="truncate max-w-[220px]" title={row.email}>
                                  {row.name || row.email || row.user_id.slice(0, 8)}
                                </span>
                                {row.role && row.role !== 'viewer' && (
                                  <Badge className={`${roleBadgeClass(row.role)} text-[9px] py-0 px-1.5 h-4`}>
                                    {row.role}
                                  </Badge>
                                )}
                              </div>
                              {row.email && (
                                <span className="text-[11px] text-muted-foreground truncate max-w-[220px]" title={row.email}>
                                  {row.email}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            <span className={atCap ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}>
                              {row.daily_count || 0}/{summary?.daily_cap || 2}
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {row.total_calls || 0}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                            {row.total_success || 0}
                            {successRatePct !== null && (
                              <span className="text-[10px] text-muted-foreground ml-1">
                                ({successRatePct}%)
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            <span className={
                              (row.total_failure || 0) > 0
                                ? 'text-rose-600 dark:text-rose-400'
                                : 'text-muted-foreground'
                            }>
                              {row.total_failure || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-foreground">
                            ${(row.estimated_cost_usd || 0).toFixed(2)}
                          </TableCell>
                          <TableCell title={fmtAbs(row.last_call_at)} className="text-[12px] text-muted-foreground">
                            {fmtTime(row.last_call_at)}
                          </TableCell>
                          <TableCell title={fmtAbs(row.last_failure_at)} className="text-[12px]">
                            {row.last_failure_code ? (
                              <div className="flex items-center gap-1.5">
                                <AlertCircle size={11} className="text-rose-500 flex-shrink-0" />
                                <code className="text-[10px] bg-rose-500/10 text-rose-700 dark:text-rose-300 px-1.5 py-0.5 rounded">
                                  {row.last_failure_code}
                                </code>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        <p className="text-[11px] text-muted-foreground text-center">
          Auto-refreshes every 30s. Cost is an estimate @ ${summary?.cost_per_scrape_usd ?? 0.01}
          per successful scrape (profile + posts actor combined).
        </p>
      </div>
    </AppShell>
  );
}
